import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Box, Grid } from '@mui/material';
import { FormModal } from 'components/molecules/Modal';
import { FormSelect } from 'components/molecules/FormSelect';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Elabels } from 'pages/CaseManagementPage/CasesPage/helpers';
import { useCase } from '../../hooks';
import { ActionTypes } from 'state/actions/alert';
import { useAlert } from 'models/alertContext';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { IFormElementOptionProp } from 'models/form';
import { FormSearchSelect } from 'components/molecules/FormSearchSelect';

interface IFormValues {
  workerUniqueId: string;
}
const initialFormValues = { workerUniqueId: '' };

interface IAssignCaseForm {
  caseId: string;
  currentWorkerId?: string;
  close: () => void;
  isOpen: boolean;
  updateData: () => void;
}

const AssignCaseForm = ({
  caseId,
  currentWorkerId,
  close,
  isOpen,
  updateData,
}: IAssignCaseForm) => {
  const { t } = useTranslations();
  const { handleAssignCase, caseWorkersData, getWorkersData } = useCase();
  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const [caseWorkers, setCaseWorkers] = useState<IFormElementOptionProp[]>([]);
  const { setAlert, clearAlert, modalAlert } = useAlert();

  const schema = yup.object({
    workerUniqueId: yup.string().required(t('general.field.validation.required')),
  });
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<IFormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IFormValues>,
  });

  useEffect(() => {
    if (caseId && isOpen) {
      getWorkersData(caseId);
    }
  }, [caseId, isOpen]);

  useEffect(() => {
    if (currentWorkerId && caseWorkers) {
      reset({
        workerUniqueId:
          caseWorkers
            .find((item) => item.value.toString() === currentWorkerId.toString())
            ?.value.toString() ?? undefined,
      });
    }
  }, [currentWorkerId, caseWorkers]);

  useEffect(() => {
    clearAlert(ActionTypes.CLEAR_MODAL_ALERT);
  }, [isOpen]);

  useEffect(() => {
    if (caseWorkersData?.length) {
      const data = caseWorkersData.map((worker) => ({
        label: `${worker.firstName} ${worker.lastName}, ${worker.displayUniqueId}`,
        value: worker.uniqueId,
      }));
      setCaseWorkers(data);
    }
  }, [caseWorkersData]);

  const onClickApproveAssignCase = async (data: any) => {
    if (isSubmitLoading) return false;
    if (data.workerUniqueId === currentWorkerId) {
      setAlert(ActionTypes.SET_MODAL_ALERT, {
        text: t('general.noChanged.warning.message'),
        variant: EAlertVariants.warning,
      });
    } else {
      setSubmitLoading(true);
      const res = await handleAssignCase(caseId, data.workerUniqueId);
      if (res) {
        updateData();
      }
      reset(initialFormValues);
      close();
      clearAlert(ActionTypes.CLEAR_MODAL_ALERT);
      setSubmitLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onClickApproveAssignCase)}>
      <FormModal
        open={isOpen}
        onClickClose={() => {
          close();
          reset(initialFormValues);
        }}
        onClickApprove={handleSubmit(onClickApproveAssignCase)}
        approveButtonLabel={Elabels.assign}
        title={t(Elabels.assignCase)}
        titleLeft
        text={t(Elabels.assignSelectedCase)}
        minWidth="440px"
      >
        <Grid sx={{ gap: 4, mb: 2.5 }}>
          <Grid size={{ xs: 12 }}>
            <FormSearchSelect
              options={caseWorkers}
              name="workerUniqueId"
              label={t(Elabels.worker)}
              control={control}
              errors={errors}
              required
              id="assign_case_form_worker_unique_id-select"
            />
          </Grid>
        </Grid>
        {modalAlert && (
          <Grid size={{ xs: 12 }}>
            <Box>
              <Alert text={modalAlert.text} variant={modalAlert.variant} />
            </Box>
          </Grid>
        )}
      </FormModal>
    </form>
  );
};

export default AssignCaseForm;
